
import { AppDataType, GameConfigType } from "@/types";
import { Constants } from "@/constant/Constants";
import TestCheck from "@/components/TestCheck.vue";
import TestSelect from "@/components/TestSelect.vue";
import { Game } from "@/data/Game";
import AbstractVue from "@/views/AbstractVue";
import { gsap } from "gsap";
import { rsort } from "semver";
import { Options } from "vue-class-component";
import { loadGameVersions, loadGameConfigs } from '@/data/Utils';
import SearchableSelect from "./SearchableSelect.vue";

@Options({
  components: {
    SearchableSelect,
    TestSelect,
    TestCheck,
  },
})
export default class Testing extends AbstractVue {
  sequence = "";

  created(): void {
    if (this.parameters.game == null) {
      this.$router.push("/");
    }

    if (this.parameters.game.selectedConfig.outcomes.length > 0) {
      this.sequence =
        this.parameters.game.selectedConfig.selectedOutcome.sequence;
    }
  }

  sendOutcome(): void {
    const request = {
      playerId: this.parameters.krakenPlayerId,
      sequence: this.sequence,
    };
    const url: string =
      this.parameters.environment.serverAddress +
      this.parameters.game.serverPath +
      "setOutcome";
    this.axios
      .post(url, request)
      .then(() => {
        this.$emit("sendSuccess");
      })
      .catch((err) => {
        console.log(err);
        this.$emit("sendFail");
      });
  }

  async clearOutcome(): Promise<void> {
    const request = {
      playerId: this.parameters.krakenPlayerId,
    };
    const url: string =
      this.parameters.environment.serverAddress +
      this.parameters.game.serverPath +
      "clearOutcome";
    await this.axios
      .post(url, request)
      .then(() => {
        this.$emit("clearSuccess");
      })
      .catch(() => {
        this.$emit("clearFail");
      });
  }

  resetUser(): void {
    this.parameters.resetPlayerId();
    this.loadGame();
  }

  loadGame(): void {
    this.clearOutcome();
    if (this.parameters.game.selectedConfig.outcomes.length > 0) {
      this.sequence =
        this.parameters.game.selectedConfig.selectedOutcome.sequence;
    }
  }

  async updateGameConfigs(game: Game): Promise<void> {
    const configs = await loadGameConfigs(game, this.$http)
    if (configs.length > 0) {
      game.setGameConfig(configs);
      console.log("[DEBUG]: ", game.selectedConfig);
    } else {
      this.$router.push("/");
    }
    game.buildPrefix = this.parameters.game.buildPrefix;

    const versions = await loadGameVersions(game, this.$http);
    game.setGameVersions(versions);
    this.parameters.game = game; 
    this.loadGame();
  }

  addFreeRounds(): void {
    const request = {
      playerId: this.parameters.krakenPlayerId,
      stake: this.parameters.freeRoundStake,
      numberOfRounds: this.parameters.freeRoundTotal,
    };
    const url: string =
      this.parameters.environment.serverAddress +
      "/external/play/addFreeRounds";
    this.axios
      .post(url, request)
      .then((_) => {
        this.$emit("freeRoundsSuccess");
      })
      .catch((_) => {
        this.$emit("freeRoundsFail");
      });
  }

  async autoComplete(): Promise<void> {
    const request = {
      gameRoundId: this.parameters.autoCompleteGameRoundId,
    };
    await this.clearOutcome();
    const url: string =
      this.parameters.environment.serverAddress +
      this.parameters.game.serverPath +
      "autoComplete";

    await this.axios
      .post(url, request)
      .then((_) => {
        this.$emit("autoCompleteSuccess");
      })
      .catch((_) => {
        this.$emit("autoCompleteFailure");
      });
  }

  popout(): void {
    this.$emit("popout");
  }

  launchEmulator(): void {
    this.$emit("launch-emulator");
  }

  setSocial(): void {
    this.parameters.language = this.languages.find((el) => el.id == "en-SC");
    this.parameters.currency = "SC.";
  }

  beforeEnter(el: HTMLElement): void {
    el.style.opacity = "0";
    el.style.transform = "translateY(200px)";
  }

  enter(el: HTMLElement, done: () => void): void {
    gsap.to(el, {
      opacity: 1,
      y: 0,
      duration: 0.5,
      onComplete: done,
      delay: 0.2 * +el.dataset.index,
    });
  }

  get gameOptions(): Game[] {
    if (this.isPrototype) {
      return [this.parameters.game];
    } else {
      return this.games;
    }
  }
}
